/**
 * Showcase Slider — editor component.
 *
 * Two views, one toolbar switch.
 *
 * Editing stacks slides as media/panel pairs. Deliberately not a preview of the
 * frontend composition: there the panel is a fixed frame showing one slide at a
 * time, and reproducing that while authoring would hide every slide but one —
 * an author editing slide three needs to see slide three.
 *
 * Previewing is the other half of that trade. The stacked view can show what a
 * slide contains but never what the block does between slides, so the toolbar
 * hands over to PreviewComposition, which rebuilds the frontend regions and
 * steps through them. Placement follows the Scrolling (marquee) block: this is
 * a block-level mode rather than a setting, so it belongs in the toolbar.
 *
 * Unlike the marquee, previewing here is always an explicit press — a marquee
 * that never moves does not read as a marquee, but a showcase that quietly
 * collapsed to one slide whenever it lost focus would hide the content the
 * author came for.
 */
import { __ } from '@wordpress/i18n';
import {
  useBlockProps,
  useInnerBlocksProps,
  InspectorControls,
  BlockControls,
  store as blockEditorStore,
} from '@wordpress/block-editor';
import { ToolbarButton, ToolbarGroup } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { useCallback, useState } from '@wordpress/element';

import LayoutPanel from './InspectorControls/LayoutPanel';
import PreviewComposition from './PreviewComposition';
import { SHOWCASE_STARTER_SLIDES } from '../../_shared/showcase-slide-defaults';
import useStepPlayback from '../../_shared/slider-preview-playback';
import '../editor.scss';

/*
 * The block has no autoplay setting — on the frontend a reader advances it — so
 * the preview picks its own cadence. Long enough to read a heading before the
 * panel moves on, which is what the pacing has to demonstrate.
 */
const PREVIEW_DELAY = 3200;

/*
 * Each slide is shown whole here, one under another; on the frontend they pass
 * through a panel that holds still. That difference is the one thing a static
 * canvas cannot show, so it is said in words — per layout, because what the
 * panel does differs in each.
 */
const HINTS = {
  editorial: __(
    'Slides are shown one under another. On the frontend the coloured panel holds its place while the text slides through it, and the next photo previews at the edge.',
    'brandy-blocks'
  ),
  overlay: __(
    'Slides are shown one under another. On the frontend the card holds its place over a full-width photo while the text slides through it.',
    'brandy-blocks'
  ),
  stacked: __(
    'Slides are shown one under another. On the frontend the caption bar holds its place beneath the photo while the text slides through it.',
    'brandy-blocks'
  ),
};

export default function Edit({ attributes, setAttributes, clientId }) {
  const { layout, panelSide, panelWidth, mediaHeight } = attributes;

  /*
   * Null until the author presses the toolbar button, which then pins their
   * choice — the same override the Scrolling (marquee) block keeps. Left alone,
   * a block nobody is editing shows what it does rather than how it is built:
   * the stacked view is for authoring, and an idle canvas has no author in it.
   */
  const [previewOverride, setPreviewOverride] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const { slides, isBeingEdited } = useSelect(
    (select) => {
      const store = select(blockEditorStore);

      return {
        slides: store.getBlocks(clientId),
        // Reaching into the block is the signal the author came to edit, and
        // the composition shows one slide at a time — it would hide the very
        // slide they clicked into.
        isBeingEdited:
          store.isBlockSelected(clientId) ||
          store.hasSelectedInnerBlock(clientId, true),
      };
    },
    [clientId]
  );

  // Mirrors render.php: a slide short of its two halves is not a slide.
  const slideCount = slides.filter((slide) => (slide.innerBlocks || []).length >= 2).length;

  /*
   * A block with nothing in it stays in the editing view whatever the toggle
   * says: the inner-blocks template only mounts when that view renders, so
   * previewing an empty block would leave it empty for good — the same guard
   * the marquee needs.
   */
  const showPreview = (previewOverride ?? !isBeingEdited) && slideCount > 0;

  // Previewing is what asks for the motion; the pause button is the exception.
  const isPlaying = showPreview && !isPaused && slideCount > 1;

  /*
   * The preview wraps even though the frontend's Embla runs `loop: false`. That
   * setting exists because the follower tracks read scrollProgress(), which
   * stops being linear once Embla wraps — a constraint on the engine, not a
   * claim that slide one is unreachable. Resting forever on the last slide
   * would leave every idle canvas showing the end of the block.
   */
  const { index, goTo } = useStepPlayback({
    count: slideCount,
    delay: PREVIEW_DELAY,
    playing: isPlaying,
    loop: true,
  });

  /*
   * `is-editor-preview` is the stacked view's own class — editor.scss hangs the
   * slide framing and a `display: block` off it. The preview rebuilds the
   * frontend regions instead, which need style.scss's flex row, so the class
   * has to come off rather than be overridden.
   */
  const blockProps = useBlockProps({
    className: [
      'brandy-showcase-slider',
      showPreview ? 'is-editor-playing embla' : 'is-editor-preview',
      `is-showcase-${layout || 'editorial'}`,
    ].join(' '),
    'data-panel-side': panelSide,
    style: {
      '--showcase-panel-width': `${panelWidth}%`,
      '--showcase-media-height': mediaHeight || '540px',
    },
  });

  const innerBlocksProps = useInnerBlocksProps(
    { className: 'brandy-showcase-slider__slides' },
    {
      allowedBlocks: ['brandy/showcase-slide'],
      template: SHOWCASE_STARTER_SLIDES,
      orientation: 'vertical',
    }
  );

  const handleToggle = useCallback(() => {
    if (!showPreview) {
      // Entering the preview starts from the first slide; picking up wherever
      // a previous run stopped looks like the block lost its place.
      goTo(0);
      setIsPaused(false);
    }

    setPreviewOverride(!showPreview);
  }, [showPreview, goTo]);

  // Driving the arrows is a request to look at one slide, not to watch.
  const handleSelect = useCallback(
    (next) => {
      setIsPaused(true);
      goTo(next);
    },
    [goTo]
  );

  return (
    <>
      <BlockControls>
        <ToolbarGroup>
          <ToolbarButton
            icon={showPreview ? 'edit' : 'controls-play'}
            label={showPreview ? __('Edit', 'brandy-blocks') : __('Preview', 'brandy-blocks')}
            onClick={handleToggle}
            isPressed={showPreview}
            disabled={slideCount === 0}
          />
          {showPreview && (
            <ToolbarButton
              icon={isPlaying ? 'controls-pause' : 'controls-play'}
              label={
                isPlaying
                  ? __('Pause preview', 'brandy-blocks')
                  : __('Play preview', 'brandy-blocks')
              }
              onClick={() => setIsPaused((paused) => !paused)}
              isPressed={isPlaying}
              disabled={slideCount < 2}
            />
          )}
        </ToolbarGroup>
      </BlockControls>
      <InspectorControls>
        <LayoutPanel attributes={attributes} setAttributes={setAttributes} />
      </InspectorControls>
      {showPreview ? (
        <PreviewComposition
          blockProps={blockProps}
          slides={slides}
          attributes={attributes}
          index={index}
          onSelect={handleSelect}
        />
      ) : (
        <div {...blockProps}>
          {/*
           * Above the slides, not below: it explains how to read what follows,
           * and a note that only appears after scrolling past three slides has
           * already missed its moment.
           */}
          <p className="brandy-showcase-slider__hint">{HINTS[layout] || HINTS.editorial}</p>
          <div {...innerBlocksProps} />
        </div>
      )}
    </>
  );
}
